import React, { useEffect, useState } from 'react';
import { Search, Calendar, DollarSign, User, Building2, Navigation, Send, ChevronDown, Plus, X, FileText, Edit2, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Deduction {
  id: string;
  driver_name: string;
  date: string;
  type: string;
  amount: number;
  charged_amount?: number;
  remaining_amount?: number;
  driver_paid: boolean;
  paid_date?: string;
  statement_id?: string;
  source: 'custom' | 'hotel' | 'lyft' | 'airplane';
  raw_id: string;
  is_custom: boolean;
}

type GroupBy = 'week' | 'driver';

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
}

export function DeductionsView() {
  const toast = useToast();
  const [deductions, setDeductions] = useState<Deduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('driver');
  const [selectedDriver, setSelectedDriver] = useState<string>('all');
  const [paymentStatus, setPaymentStatus] = useState<'unpaid' | 'paid' | 'all'>('unpaid');
  const [showModal, setShowModal] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState<Deduction | null>(null);
  const [formData, setFormData] = useState({
    driver_id: '',
    type: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadDeductions();
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name')
        .order('first_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  }

  const handleEditDeduction = (deduction: Deduction) => {
    if (!deduction.is_custom) {
      toast.error('Can only edit custom deductions created on this page');
      return;
    }
    setSelectedDeduction(deduction);
    setFormData({
      driver_id: '',
      type: deduction.type,
      amount: deduction.amount.toString(),
      date: deduction.date,
    });
    setDriverSearch(deduction.driver_name);
    setShowEditModal(true);
  };

  const handleDeleteDeduction = (deduction: Deduction) => {
    setSelectedDeduction(deduction);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedDeduction) return;

    const sourceToTable: Record<Deduction['source'], string> = {
      custom: 'driver_deductions',
      hotel: 'hotel_reimbursements',
      lyft: 'lyft_reimbursements',
      airplane: 'airplane_reimbursements',
    };

    try {
      const table = sourceToTable[selectedDeduction.source];
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', selectedDeduction.raw_id);

      if (error) throw error;

      toast.success('Deduction deleted successfully');
      setShowDeleteModal(false);
      setSelectedDeduction(null);
      loadDeductions();
    } catch (error) {
      console.error('Error deleting deduction:', error);
      toast.error('Failed to delete deduction');
    }
  };

  const handleUpdateDeduction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeduction) return;

    try {
      const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === driverSearch);
      const driverId = driver?.id;
      const newAmount = parseFloat(formData.amount);

      // Keep remaining_amount in step with the edited amount. Otherwise the
      // settlement wizard caps the charge at the old remaining balance.
      const { data: current } = await supabase
        .from('driver_deductions')
        .select('charged_amount, is_recurring, status')
        .eq('id', selectedDeduction.raw_id)
        .single();

      const updateData: any = {
        driver_id: driverId,
        deduction_date: formData.date,
        amount: newAmount,
        description: formData.type,
      };
      if (current && !current.is_recurring) {
        const remaining = Math.max(0, newAmount - (current.charged_amount || 0));
        updateData.remaining_amount = remaining;
        if (remaining > 0 && current.status === 'paid') {
          updateData.status = 'pending';
          updateData.paid_date = null;
        }
      }

      const { error } = await supabase
        .from('driver_deductions')
        .update(updateData)
        .eq('id', selectedDeduction.raw_id);

      if (error) throw error;

      toast.success('Deduction updated successfully');
      setShowEditModal(false);
      setSelectedDeduction(null);
      setFormData({
        driver_id: '',
        type: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
      });
      setDriverSearch('');
      loadDeductions();
    } catch (error) {
      console.error('Error updating deduction:', error);
      toast.error('Failed to update deduction');
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      const deductionData: any = {
        driver_id: formData.driver_id,
        deduction_date: formData.date,
        amount: parseFloat(formData.amount),
        description: formData.type,
        deduction_type: 'custom',
        status: 'pending',
        frequency: 'one_time',
        is_recurring: false,
      };

      const { error } = await supabase
        .from('driver_deductions')
        .insert(deductionData);

      if (error) throw error;

      toast.success('Deduction added successfully');
      setShowModal(false);
      setFormData({
        driver_id: '',
        type: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
      });
      setDriverSearch('');
      loadDeductions();
    } catch (error) {
      console.error('Error adding deduction:', error);
      toast.error('Failed to add deduction');
    }
  }

  async function loadDeductions() {
    try {
      setLoading(true);

      // Fetch custom deductions from driver_deductions table
      const { data: customDeductions, error: customError } = await supabase
        .from('driver_deductions')
        .select(`
          id,
          driver_id,
          deduction_type,
          amount,
          charged_amount,
          remaining_amount,
          is_recurring,
          description,
          deduction_date,
          status,
          paid_date,
          statement_id,
          drivers (
            first_name,
            last_name
          )
        `)
        .order('deduction_date', { ascending: false });

      if (customError) throw customError;

      // Fetch all hotel reimbursements
      const { data: hotelData, error: hotelError } = await supabase
        .from('hotel_reimbursements')
        .select('*')
        .order('date', { ascending: false });

      if (hotelError) throw hotelError;

      // Fetch all lyft reimbursements
      const { data: lyftData, error: lyftError } = await supabase
        .from('lyft_reimbursements')
        .select('*')
        .order('date', { ascending: false });

      if (lyftError) throw lyftError;

      // Fetch all airplane reimbursements
      const { data: airplaneData, error: airplaneError } = await supabase
        .from('airplane_reimbursements')
        .select('*')
        .order('date', { ascending: false });

      if (airplaneError) throw airplaneError;

      // Aggregate paid-to-date for reimbursements from statement_line_items
      // (driver_deductions rows track charged/remaining on the row itself; reimbursement tables do not).
      const reimbursementIds = [
        ...(hotelData || []).map((i: any) => i.id),
        ...(lyftData || []).map((i: any) => i.id),
        ...(airplaneData || []).map((i: any) => i.id),
      ];
      const paidByItemId = new Map<string, number>();
      if (reimbursementIds.length > 0) {
        const { data: priorLineItems } = await supabase
          .from('statement_line_items')
          .select('item_id, amount')
          .eq('item_type', 'deduction')
          .in('item_id', reimbursementIds);
        (priorLineItems || []).forEach((li: any) => {
          const prev = paidByItemId.get(li.item_id) || 0;
          paidByItemId.set(li.item_id, prev + Math.abs(li.amount || 0));
        });
      }

      // Combine all deductions
      const allDeductions: Deduction[] = [
        // Custom deductions from driver_deductions table
        ...(customDeductions || []).map((item: any) => ({
          id: `custom-${item.id}`,
          raw_id: item.id,
          driver_name: item.drivers ? `${item.drivers.first_name} ${item.drivers.last_name}` : 'Unknown',
          date: item.deduction_date,
          type: item.description,
          amount: item.amount,
          charged_amount: item.charged_amount || 0,
          // One-time deductions: remaining is always amount - charged. The stored
          // remaining_amount goes stale when the amount is edited after a charge
          // (or a statement delete), so derive it. Recurring rows keep the stored
          // value because there remaining tracks the total, not the installment.
          remaining_amount: item.is_recurring
            ? (item.remaining_amount !== null ? item.remaining_amount : item.amount)
            : Math.max(0, (item.amount || 0) - (item.charged_amount || 0)),
          driver_paid: item.status === 'paid',
          paid_date: item.paid_date,
          statement_id: item.statement_id,
          source: 'custom' as const,
          is_custom: true,
        })),
        // Hotel reimbursements
        ...(hotelData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `hotel-${item.id}`,
            raw_id: item.id,
            driver_name: item.driver_name,
            date: item.date,
            type: item.hotel_description || 'Hotel',
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            driver_paid: item.driver_paid,
            paid_date: item.paid_date,
            statement_id: item.statement_id,
            source: 'hotel' as const,
            is_custom: false,
          };
        }),
        ...(lyftData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `lyft-${item.id}`,
            raw_id: item.id,
            driver_name: item.driver_name,
            date: item.date,
            type: 'Lyft Ride',
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            driver_paid: item.driver_paid,
            paid_date: item.paid_date,
            statement_id: item.statement_id,
            source: 'lyft' as const,
            is_custom: false,
          };
        }),
        ...(airplaneData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `airplane-${item.id}`,
            raw_id: item.id,
            driver_name: item.driver_name,
            date: item.date,
            type: item.airline_company || 'Airplane',
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            driver_paid: item.driver_paid,
            paid_date: item.paid_date,
            statement_id: item.statement_id,
            source: 'airplane' as const,
            is_custom: false,
          };
        }),
      ];

      // Sort by date descending
      allDeductions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setDeductions(allDeductions);
    } catch (error) {
      console.error('Error loading deductions:', error);
      toast.error('Failed to load deductions');
    } finally {
      setLoading(false);
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hotel':
        return <Building2 className="w-4 h-4" />;
      case 'lyft':
        return <Navigation className="w-4 h-4" />;
      case 'airplane':
        return <Send className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'hotel':
        return 'bg-blue-100 text-blue-800';
      case 'lyft':
        return 'bg-purple-100 text-purple-800';
      case 'airplane':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getWeekNumber = (date: Date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const getWeekRange = (date: Date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(date.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Filter deductions
  const filteredDeductions = deductions.filter(deduction => {
    const matchesSearch = 
      deduction.driver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deduction.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDriver = selectedDriver === 'all' || deduction.driver_name === selectedDriver;
    
    const matchesPaymentStatus = 
      paymentStatus === 'all' ||
      (paymentStatus === 'paid' && deduction.driver_paid) ||
      (paymentStatus === 'unpaid' && !deduction.driver_paid);
    
    return matchesSearch && matchesDriver && matchesPaymentStatus;
  });

  // Get unique driver names for filter
  const uniqueDrivers = Array.from(new Set(deductions.map(d => d.driver_name))).sort();

  // Group deductions
  const groupedDeductions = () => {
    if (groupBy === 'driver') {
      const groups = new Map<string, Deduction[]>();
      filteredDeductions.forEach(deduction => {
        if (!groups.has(deduction.driver_name)) {
          groups.set(deduction.driver_name, []);
        }
        groups.get(deduction.driver_name)!.push(deduction);
      });
      return Array.from(groups.entries()).map(([driverName, items]) => ({
        key: driverName,
        label: driverName,
        items,
        total: items.reduce((sum, item) => sum + item.amount, 0),
      }));
    } else {
      // Group by week
      const groups = new Map<string, Deduction[]>();
      filteredDeductions.forEach(deduction => {
        const date = new Date(deduction.date);
        const weekKey = `${date.getFullYear()}-W${getWeekNumber(date)}`;
        const weekLabel = getWeekRange(new Date(deduction.date));
        
        if (!groups.has(weekKey)) {
          groups.set(weekKey, []);
        }
        groups.get(weekKey)!.push(deduction);
      });
      
      return Array.from(groups.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([weekKey, items]) => {
          const weekLabel = getWeekRange(new Date(items[0].date));
          return {
            key: weekKey,
            label: weekLabel,
            items,
            total: items.reduce((sum, item) => sum + item.amount, 0),
          };
        });
    }
  };

  const groups = groupedDeductions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deductions</h1>
            <p className="text-sm text-gray-500 mt-1">Driver-paid expenses from Hotel, Lyft, and Airplane tickets</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add Deduction
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by driver or deduction type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Driver Filter */}
          <div className="relative flex-1">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
            >
              <option value="all">All Drivers</option>
              {uniqueDrivers.map(driver => (
                <option key={driver} value={driver}>{driver}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
          </div>

          {/* Payment Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setPaymentStatus('unpaid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                paymentStatus === 'unpaid'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Not Paid
            </button>
            <button
              onClick={() => setPaymentStatus('paid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                paymentStatus === 'paid'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Paid
            </button>
            <button
              onClick={() => setPaymentStatus('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                paymentStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
          </div>

          {/* Group By Toggle Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setGroupBy('driver')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                groupBy === 'driver'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <User className="w-4 h-4" />
              By Driver
            </button>
            <button
              onClick={() => setGroupBy('week')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                groupBy === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Calendar className="w-4 h-4" />
              By Week
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {groups.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium text-gray-900">No deductions found</p>
            <p className="text-sm text-gray-500 mt-1">Driver-paid expenses will appear here</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(group => (
              <div key={group.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Group Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">{group.label}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{group.items.length} items</span>
                      <span className="text-lg font-bold text-gray-900">
                        ${group.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Group Items */}
                <div className="divide-y divide-gray-200">
                  {group.items.map(deduction => (
                    <div key={deduction.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Type Badge */}
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {deduction.type}
                          </span>

                          {/* Driver Name (only show if grouped by week) */}
                          {groupBy === 'week' && (
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{deduction.driver_name}</span>
                            </div>
                          )}

                          {/* Date */}
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {new Date(deduction.date).toLocaleDateString()}
                            </span>
                          </div>

                          {/* Paid Date (if paid) */}
                          {deduction.paid_date && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Paid:</span>
                              <span className="text-xs font-medium text-green-700">
                                {new Date(deduction.paid_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {/* Statement Link (if paid) */}
                          {deduction.statement_id && (
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-400" />
                              <a
                                href={`#/accounting/statements?id=${deduction.statement_id}`}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Statement #{deduction.statement_id.slice(0, 8)}
                              </a>
                            </div>
                          )}

                        </div>

                        {/* Amount and Status */}
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-semibold text-gray-900">
                                {deduction.amount.toFixed(2)}
                              </span>
                            </div>
                            {deduction.charged_amount !== undefined && deduction.charged_amount > 0 && (
                              <div className="flex flex-col text-xs ml-5 mt-1">
                                <span className="text-green-600">
                                  Paid: ${deduction.charged_amount.toFixed(2)}
                                </span>
                                <span className="text-orange-600 font-medium">
                                  Remaining: ${(deduction.remaining_amount || 0).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {(() => {
                            const chargedSoFar = deduction.charged_amount || 0;
                            const isPartiallyPaid = !deduction.driver_paid && chargedSoFar > 0 && chargedSoFar < deduction.amount;
                            if (deduction.driver_paid) {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Paid
                                </span>
                              );
                            }
                            if (isPartiallyPaid) {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  Partial
                                </span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Unpaid
                              </span>
                            );
                          })()}

                          <div className="flex items-center gap-2">
                            {deduction.is_custom && (
                              <button
                                onClick={() => handleEditDeduction(deduction)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit deduction"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteDeduction(deduction)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete deduction"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Deduction Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Add Deduction</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setDriverSearch('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Driver</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={driverSearch}
                    onChange={(e) => { setDriverSearch(e.target.value); setShowDriverDropdown(true); }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search driver..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                {driverSearch && showDriverDropdown && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-slate-300 rounded-lg">
                    {drivers
                      .filter(d =>
                        `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                      )
                      .map((driver) => (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, driver_id: driver.id });
                            setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                            setShowDriverDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-100 transition-colors"
                        >
                          {driver.first_name} {driver.last_name}
                        </button>
                      ))}
                  </div>
                )}
                {formData.driver_id && !driverSearch && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Driver selected
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Deduction Type</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="e.g., Hotel, Lyft, Airplane, Fuel, etc."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setDriverSearch('');
                  }}
                  className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Add Deduction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDeduction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Edit Deduction</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDeduction(null);
                  setDriverSearch('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateDeduction} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Driver</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    value={driverSearch}
                    onChange={(e) => { setDriverSearch(e.target.value); setShowDriverDropdown(true); }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search driver..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                {driverSearch && showDriverDropdown && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-slate-300 rounded-lg">
                    {drivers
                      .filter(d =>
                        `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                      )
                      .map((driver) => (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => {
                            setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                            setShowDriverDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-slate-100 transition-colors"
                        >
                          {driver.first_name} {driver.last_name}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Deduction Type</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="e.g., Hotel, Lyft, Airplane, Fuel, etc."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedDeduction(null);
                    setDriverSearch('');
                  }}
                  className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDeduction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Delete Deduction</h2>
              <p className="text-sm text-slate-600 mt-2">
                Are you sure you want to delete this deduction?
              </p>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm"><strong>Type:</strong> {selectedDeduction.type}</p>
                <p className="text-sm"><strong>Driver:</strong> {selectedDeduction.driver_name}</p>
                <p className="text-sm"><strong>Amount:</strong> ${selectedDeduction.amount.toFixed(2)}</p>
                <p className="text-sm"><strong>Date:</strong> {new Date(selectedDeduction.date).toLocaleDateString()}</p>
              </div>
              <p className="text-sm text-red-600 mt-3 font-semibold">
                This action cannot be undone!
              </p>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDeduction(null);
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Deduction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
